import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { projectService, Project } from '../services/projectService';
import { useActiveProject } from '../hooks/useActiveProject';
import CreateProjectModal from './CreateProjectModal';
import EditProjectModal from './EditProjectModal';
import Messenger from './Messenger';
import Icon, { IconName } from './Icon';
import '../styles/Layout.css';
import '../styles/Messenger.css';

interface LayoutProps {
  children: React.ReactNode;
}

interface SubMenuItem {
  label: string;
  path: string;
}

interface MenuItem {
  iconName: IconName;
  label: string;
  path: string;
  comingSoon?: boolean;
  subItems?: SubMenuItem[];
}

// Warm up the Cloud Function as soon as the layout mounts (i.e. any authenticated page),
// so it's already running by the time the user clicks Task Boards.
let _warmedUp = false;
function warmUpApi() {
  if (_warmedUp) return;
  _warmedUp = true;
  fetch('/api/health').catch(() => {});
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, signOut, orgRole, currentOrg } = useAuth();
  const { can } = usePermissions();
  const [messengerOpen, setMessengerOpen] = useState(false);
  const [fabUnread, setFabUnread]         = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth > 768);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [expandedMenu, setExpandedMenu] = useState<string | null>(null);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [projectMenuOpen, setProjectMenuOpen] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [showCreateProjectModal, setShowCreateProjectModal] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const { activeProjectId, setActiveProjectId } = useActiveProject();

  // Fire-and-forget: keep the Cloud Function warm from the first authenticated page
  useEffect(() => { warmUpApi(); }, []);

  // Load the org's projects for the switcher.
  //
  // Keyed on currentOrg, NOT [] — orgStore.getOrgId() is populated
  // asynchronously by AuthContext after the session resolves. Running this on
  // mount raced that: getProjects() threw "No organization found" (or, before
  // the org filter existed, cached an empty list for 2 minutes) and the
  // switcher stayed empty until a hard reload.
  useEffect(() => {
    if (!currentOrg) return;
    let cancelled = false;

    projectService.getProjects()
      .then(rows => { if (!cancelled) setProjects(rows); })
      .catch(err => {
        if (!cancelled) console.error('[Layout] failed to load projects:', err);
      });

    return () => { cancelled = true; };
  }, [currentOrg]);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      setIsSidebarOpen(!mobile);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!profileMenuOpen && !projectMenuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (profileMenuOpen && !target.closest('.topbar-profile')) {
        setProfileMenuOpen(false);
      }
      if (projectMenuOpen && !target.closest('.topbar-project-switcher')) {
        setProjectMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [profileMenuOpen, projectMenuOpen]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const handleLogoutClick = () => {
    setShowLogoutConfirm(true);
    if (isMobile) setIsSidebarOpen(false);
  };

  const toggleSidebar = () => setIsSidebarOpen(prev => !prev);

  const coreMenuItems: MenuItem[] = [
    { iconName: 'dashboard',   label: 'Dashboard',      path: '/dashboard' },
  ];

  // Onboarding nav group — sits above Customers. Gated behind the
  // 'onboarding' permission (on by default for owner/admin only).
  if (can('onboarding')) {
    coreMenuItems.push({
      iconName: 'bookOpen',
      label: 'Onboarding',
      path: '/onboarding/companies',
      subItems: [
        { label: 'Companies',  path: '/onboarding/companies' },
        { label: 'Templates',  path: '/onboarding/templates' },
        { label: 'Tasks',      path: '/onboarding/tasks' },
        { label: 'Messages',   path: '/onboarding/messages' },
        { label: 'Forms',      path: '/onboarding/forms' },
        { label: 'Documents',  path: '/onboarding/documents' },
        { label: 'Invoicing',  path: '/onboarding/invoicing' },
      ],
    });
  }

  // Customers nav item (with Companies/Users sub-items) is only visible to
  // owners/admins by default — gated behind the 'customers' permission.
  if (can('customers')) {
    coreMenuItems.push({
      iconName: 'customers',
      label: 'Customers',
      path: '/customers/companies',
      subItems: [
        { label: 'Companies', path: '/customers/companies' },
        { label: 'Users',     path: '/customers/users' },
      ],
    });
  }

  // Product nav group — each sub-item gated behind its own permission,
  // visible to owners/admins by default and disabled for custom roles
  // until explicitly granted.
  const productSubItems = [
    { label: 'Insights',    path: '/product/insights',    perm: 'product_insights' as const },
    { label: 'Prioritize',  path: '/product/prioritize',  perm: 'product_prioritize' as const },
    { label: 'Features',    path: '/product/features',    perm: 'product_features' as const },
    { label: 'Initiatives', path: '/product/initiatives', perm: 'product_initiatives' as const },
    { label: 'Releases',    path: '/product/releases',    perm: 'product_releases' as const },
    { label: 'Objectives',  path: '/product/objectives',  perm: 'product_objectives' as const },
  ].filter(item => can(item.perm));

  if (productSubItems.length > 0) {
    coreMenuItems.push({
      iconName: 'product',
      label: 'Product',
      path: productSubItems[0].path,
      subItems: productSubItems.map(({ label, path }) => ({ label, path })),
    });
  }

  coreMenuItems.push(
    { iconName: 'taskboard',  label: 'Task Boards',    path: '/taskboards' },
    { iconName: 'whiteboard', label: 'Whiteboards',    path: '/whiteboards' },
    { iconName: 'docs',       label: 'Docs Writer',    path: '/docs-writer' },
    { iconName: 'calendar',   label: 'Meeting Plan',   path: '/meeting-plan' },
  );

  // Org Management (existing /users page — team members) is only visible
  // when the user has the 'users' permission
  const menuItems: MenuItem[] = can('users')
    ? [...coreMenuItems, { iconName: 'users', label: 'Org Management', path: '/users' }]
    : coreMenuItems;

  const displayName = (user?.user_metadata?.full_name as string | undefined) || user?.email || 'Account';
  const avatarInitial = displayName.charAt(0).toUpperCase();
  const roleLabel = orgRole ? orgRole.charAt(0).toUpperCase() + orgRole.slice(1) : '';

  const activeProject = activeProjectId ? projects.find(p => p.id === activeProjectId) : null;

  const projectColors = ['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#06b6d4'];
  const projectColor = (id: string) => {
    let hash = 0;
    for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
    return projectColors[hash % projectColors.length];
  };

  return (
    <>
    <div className="layout">
      {isMobile && isSidebarOpen && (
        <div className="sidebar-overlay" onClick={() => setIsSidebarOpen(false)} />
      )}

      <aside
        className={`sidebar ${isSidebarOpen ? 'open' : 'closed'} ${isMobile ? 'mobile' : ''}`}
      >
        <div className="sidebar-header">
          {isSidebarOpen && (
            <div className="sidebar-brand">
              <span className="brand-wordmark" aria-label="All in one personal workplace">
                <span className="brand-wordmark-top">
                  <span className="brand-wordmark-all">All</span>
                  <span className="brand-wordmark-rest">in&nbsp;one</span>
                </span>
                <span className="brand-wordmark-sub">personal&nbsp;workplace</span>
              </span>
            </div>
          )}
          <button
            className="sidebar-toggle glass-button-icon"
            onClick={toggleSidebar}
            title={isSidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
          >
            <Icon name={isSidebarOpen ? 'chevronLeft' : 'menu'} size={20} strokeWidth={1.8} />
          </button>
        </div>

        <nav className="sidebar-nav">
          {menuItems.map((item) => {
            if (item.subItems && item.subItems.length > 0) {
              const isActiveGroup = item.subItems.some(sub => location.pathname === sub.path);
              const isExpanded = expandedMenu === item.label || isActiveGroup;
              return (
                <div key={item.label} className="nav-group">
                  <button
                    className={`nav-item ${isActiveGroup ? 'active' : ''}`}
                    onClick={() => {
                      setExpandedMenu(prev => prev === item.label ? null : item.label);
                      if (!isSidebarOpen) {
                        navigate(item.path);
                        if (isMobile) setIsSidebarOpen(false);
                      }
                    }}
                    title={!isSidebarOpen ? item.label : ''}
                  >
                    {item.iconName && <span className="nav-icon"><Icon name={item.iconName} size={18} strokeWidth={1.8} /></span>}
                    {isSidebarOpen && (
                      <span className="nav-label-wrapper">
                        <span className="nav-label">{item.label}</span>
                        <span className={`nav-chevron ${isExpanded ? 'expanded' : ''}`}>
                          <Icon name="chevronDown" size={14} strokeWidth={2.2} />
                        </span>
                      </span>
                    )}
                  </button>
                  {isSidebarOpen && isExpanded && (
                    <div className="nav-subitems">
                      {item.subItems.map(sub => (
                        <button
                          key={sub.path}
                          className={`nav-subitem ${location.pathname === sub.path ? 'active' : ''}`}
                          onClick={() => {
                            navigate(sub.path);
                            if (isMobile) setIsSidebarOpen(false);
                          }}
                        >
                          <span className="nav-label">{sub.label}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            }

            return (
              <button
                key={item.path}
                className={`nav-item ${location.pathname === item.path ? 'active' : ''} ${item.comingSoon ? 'coming-soon' : ''}`}
                onClick={() => {
                  if (!item.comingSoon) {
                    navigate(item.path);
                    if (isMobile) setIsSidebarOpen(false);
                  }
                }}
                title={!isSidebarOpen ? item.label : ''}
                disabled={item.comingSoon}
              >
                {item.iconName && <span className="nav-icon"><Icon name={item.iconName} size={18} strokeWidth={1.8} /></span>}
                {isSidebarOpen && (
                  <span className="nav-label-wrapper">
                    <span className="nav-label">{item.label}</span>
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </aside>

      <main className={`main-content ${isSidebarOpen && !isMobile ? 'shifted' : ''}`}>
        {isMobile && !isSidebarOpen && (
          <button className="mobile-menu-toggle glass-button-icon" onClick={() => setIsSidebarOpen(true)}>
            <Icon name="menu" size={22} strokeWidth={1.8} />
          </button>
        )}

        <header className="topbar">
          <div className="topbar-project-switcher">
            <button className="topbar-project-btn" onClick={() => setProjectMenuOpen(prev => !prev)}>
              <span className={`topbar-project-icon ${activeProject ? '' : 'all'}`} style={activeProject ? { background: projectColor(activeProject.id) } : undefined}>
                <Icon name="box" size={15} strokeWidth={2} />
              </span>
              <span className="topbar-project-text">
                <span className="topbar-project-name">{activeProject ? activeProject.name : 'All Projects'}</span>
                <span className="topbar-project-sub">{activeProject ? 'Project' : 'Workspace view'}</span>
              </span>
              <span className={`topbar-chevron ${projectMenuOpen ? 'open' : ''}`}>
                <Icon name="chevronDown" size={16} strokeWidth={2} />
              </span>
            </button>

            {projectMenuOpen && (
              <div className="topbar-project-menu">
                {can('projects') && (
                  <>
                    <button
                      className="topbar-project-menu-create"
                      onClick={() => { setProjectMenuOpen(false); setShowCreateProjectModal(true); }}
                    >
                      <span className="topbar-project-plus">+</span>
                      <span>Add New Product/Project</span>
                    </button>

                    <div className="divider"></div>
                  </>
                )}

                <button
                  className={`topbar-project-menu-item ${!activeProject ? 'active' : ''}`}
                  onClick={() => { setProjectMenuOpen(false); setActiveProjectId(null); }}
                >
                  <span className="topbar-project-icon all">
                    <Icon name="grid" size={15} strokeWidth={2} />
                  </span>
                  <span className="topbar-project-text">
                    <span className="topbar-project-name">All Projects</span>
                    <span className="topbar-project-sub">Workspace view</span>
                  </span>
                  {!activeProject && <Icon name="check" size={16} strokeWidth={2.2} />}
                </button>

                {projects.length > 0 && (
                  <>
                    <div className="topbar-project-menu-label">Products &amp; Projects</div>
                    {projects.map(p => (
                      <div key={p.id} className="topbar-project-menu-row">
                        <button
                          className={`topbar-project-menu-item ${activeProject?.id === p.id ? 'active' : ''}`}
                          onClick={() => { setProjectMenuOpen(false); setActiveProjectId(p.id); }}
                        >
                          <span className="topbar-project-icon" style={{ background: projectColor(p.id) }}>
                            <Icon name="box" size={15} strokeWidth={2} />
                          </span>
                          <span className="topbar-project-text">
                            <span className="topbar-project-name">{p.name}</span>
                            <span className="topbar-project-sub">Project</span>
                          </span>
                          {activeProject?.id === p.id && <Icon name="check" size={16} strokeWidth={2.2} />}
                        </button>
                        {can('projects') && (
                          <button
                            className="topbar-project-menu-edit"
                            title="Edit project"
                            onClick={(e) => { e.stopPropagation(); setProjectMenuOpen(false); setEditingProject(p); }}
                          >
                            <Icon name="edit" size={15} strokeWidth={1.8} />
                          </button>
                        )}
                      </div>
                    ))}
                  </>
                )}
              </div>
            )}
          </div>

          <div className="topbar-actions">
            <button className="topbar-icon-btn" title="Notifications" disabled>
              <Icon name="bell" size={19} strokeWidth={1.8} />
            </button>

            <div className="topbar-profile">
              <button className="topbar-profile-btn" onClick={() => setProfileMenuOpen(prev => !prev)}>
                <span className="topbar-avatar">{avatarInitial}</span>
                {!isMobile && (
                  <span className="topbar-profile-text">
                    <span className="topbar-profile-name">{displayName}</span>
                    {roleLabel && <span className="topbar-profile-org">{roleLabel}</span>}
                  </span>
                )}
                <span className={`topbar-chevron ${profileMenuOpen ? 'open' : ''}`}>
                  <Icon name="chevronDown" size={16} strokeWidth={2} />
                </span>
              </button>

              {profileMenuOpen && (
                <div className="topbar-profile-menu">
                  <div className="topbar-profile-menu-header">
                    <span className="topbar-avatar">{avatarInitial}</span>
                    <span className="topbar-profile-text">
                      <span className="topbar-profile-name">{displayName}</span>
                      <span className="topbar-profile-email">{user?.email}</span>
                    </span>
                  </div>
                  <div className="divider"></div>
                  <button className="topbar-profile-menu-item" onClick={() => { setProfileMenuOpen(false); navigate('/settings'); }}>
                    <Icon name="settings" size={17} strokeWidth={1.8} />
                    <span>Settings</span>
                  </button>
                  <button className="topbar-profile-menu-item logout" onClick={() => { setProfileMenuOpen(false); handleLogoutClick(); }}>
                    <Icon name="logout" size={17} strokeWidth={1.8} />
                    <span>Logout</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <div className="content-wrapper fade-in">{children}</div>
      </main>

    </div>

    {showCreateProjectModal && (
      <CreateProjectModal
        onClose={() => setShowCreateProjectModal(false)}
        onCreated={(project) => {
          setProjects(prev => [project, ...prev]);
          setShowCreateProjectModal(false);
        }}
      />
    )}

    {editingProject && (
      <EditProjectModal
        project={editingProject}
        onClose={() => setEditingProject(null)}
        onUpdated={(updated) => {
          setProjects(prev => prev.map(p => p.id === updated.id ? updated : p));
          setEditingProject(null);
        }}
      />
    )}

    {/* Messenger FAB — portal renders onto body, escaping overflow:hidden on .layout */}
    {can('messenger') && ReactDOM.createPortal(
      <>
        <button
          className="msng-fab"
          onClick={() => { setMessengerOpen(true); setFabUnread(0); }}
          title="Team Messenger"
        >
          <Icon name="messageCircle" size={22} strokeWidth={1.8} />
          {fabUnread > 0 && !messengerOpen && (
            <span className="msng-fab-badge">{fabUnread > 99 ? '99+' : fabUnread}</span>
          )}
        </button>
        {messengerOpen && <Messenger onClose={() => setMessengerOpen(false)} onUnreadChange={n => setFabUnread(n)} />}

        {/* ── Logout confirmation modal ─────────────────────── */}
        {showLogoutConfirm && (
          <div className="logout-modal-backdrop" onClick={() => setShowLogoutConfirm(false)}>
            <div className="logout-modal" onClick={e => e.stopPropagation()}>
              <div className="logout-modal-icon">
                <Icon name="logout" size={26} strokeWidth={1.8} />
              </div>
              <h3 className="logout-modal-title">Confirm Logout</h3>
              <p className="logout-modal-body">Are you sure you want to log out of your workspace?</p>
              <div className="logout-modal-actions">
                <button className="logout-cancel-btn" onClick={() => setShowLogoutConfirm(false)}>
                  Cancel
                </button>
                <button className="logout-confirm-btn" onClick={handleSignOut}>
                  Logout
                </button>
              </div>
            </div>
          </div>
        )}
      </>,
      document.body
    )}
    </>
  );
};

export default Layout;
